import { TX_TYPES } from '@exodus/activity-txs/redux/constants/tx-types';

import type { Assets } from '@/types';
import type { ActivityItem, Transaction } from './types.js';

const isFiatOnRampTx = (activityItem: ActivityItem) =>
  activityItem.fiatOrder?.orderType === 'buy';

const isFiatOffRampTx = (activityItem: ActivityItem) =>
  activityItem.fiatOrder?.orderType === 'sell';

export function getIsFailed(activityItem?: ActivityItem) {
  return !!(activityItem?.failed || activityItem?.tx?.dropped);
}

export const isSendFeeTx = (tx) =>
  tx?.tokens?.length > 0 &&
  tx?.feeAmount !== undefined &&
  tx?.coinAmount.isZero;

const defaultTitles = (activityItem: ActivityItem, assets: Assets) => {
  const { type, pending, tx } = activityItem;

  const tickers = tx?.tokens
    ?.map((tokenName) => assets[tokenName].displayTicker)
    .filter(Boolean);

  if (pending) {
    // Should we do the pending options for the ones above?
    if (tx?.selfSend) {
      return {
        title: 'Sending to self…',
        headerTitle: 'You are sending',
      };
    }

    if (type === TX_TYPES.RECEIVED) {
      return { title: 'Receiving…', headerTitle: 'You will receive' };
    }

    if (isSendFeeTx(tx) && tickers?.length) {
      return {
        title: `Fee for sending ${tickers?.join(', ')}…`,
        headerTitle: `Fee for sending ${tickers?.join(', ')}`,
      };
    }

    return { title: 'Sending…', headerTitle: 'You are sending' };
  }

  if (tx?.selfSend) {
    return { title: 'Sent to self', headerTitle: 'You sent to self' };
  }

  if (type === TX_TYPES.RECEIVED) {
    return { title: 'Received', headerTitle: 'You received' };
  }

  if (isSendFeeTx(tx) && tickers?.length) {
    const title = `Fee to send ${tickers?.join(', ')}`;
    return {
      title,
      headerTitle: title,
    };
  }

  return { title: 'Sent', headerTitle: 'You sent' };
};

export const getIcon = (activityItem: ActivityItem) => {
  const { type } = activityItem;

  if (type === TX_TYPES.EXCHANGE) {
    return 'arrow-left-arrow-right';
  }

  if (isFiatOnRampTx(activityItem) || isFiatOffRampTx(activityItem)) {
    return 'currency';
  }

  return type === TX_TYPES.RECEIVED ? 'arrow-down-left' : 'arrow-up-right';
};

export const getTitles = (activityItem: ActivityItem, assets: Assets) => {
  const { type, pending, tx } = activityItem;

  if (type === TX_TYPES.EXCHANGE) {
    if (pending) {
      return { title: 'Swapping…', headerTitle: 'You are swapping' };
    }

    return { title: 'Swapped', headerTitle: 'You swapped' };
  }

  if (isFiatOnRampTx(activityItem)) {
    if (tx?.pending) {
      return { title: 'Buying…', headerTitle: 'You are buying' };
    }

    return { title: 'Bought', headerTitle: 'You bought' };
  }

  if (isFiatOffRampTx(activityItem)) {
    if (pending) {
      return { title: 'Selling…', headerTitle: 'You are selling' };
    }

    return { title: 'Sold', headerTitle: 'You sold' };
  }

  return { ...defaultTitles(activityItem, assets) };
};

export const getValue = (tx: Transaction) => {
  if (isSendFeeTx(tx)) {
    return tx.feeAmount!.abs();
  }

  return tx.coinAmount.abs();
};
